import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { LayoutGrid, Map as MapIcon, Settings, ChevronRight, ChevronDown, Moon, Sun, Users, PanelLeftClose, PanelLeft, LogOut } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { useState, useEffect, useRef } from 'react';
import type { Team, Product } from '@sprintpulse/shared';
import clsx from 'clsx';
import { useAuth } from '../../contexts/AuthContext';
import { usePreferences } from '../../hooks/usePreferences';

function useDarkMode(serverTheme?: 'dark' | 'light' | 'system'): [boolean, () => void] {
  const [dark, setDark] = useState(() =>
    typeof window !== 'undefined'
      ? document.documentElement.classList.contains('dark')
      : false,
  );

  const toggle = (): void => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  // Sync with server preferences when available
  useEffect(() => {
    if (serverTheme && serverTheme !== 'system') {
      const shouldBeDark = serverTheme === 'dark';
      setDark(shouldBeDark);
      document.documentElement.classList.toggle('dark', shouldBeDark);
      localStorage.setItem('theme', serverTheme);
      return;
    }
    const saved = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldBeDark = saved ? saved === 'dark' : prefersDark;
    setDark(shouldBeDark);
    document.documentElement.classList.toggle('dark', shouldBeDark);
  }, [serverTheme]);

  return [dark, toggle];
}

function getCurrentQuarterLabel(): string {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  if (month <= 2) return `Q2 FY${String(year).slice(2)}`;
  if (month <= 5) return `Q3 FY${String(year).slice(2)}`;
  if (month <= 8) return `Q4 FY${String(year).slice(2)}`;
  return `Q1 FY${String(year + 1).slice(2)}`;
}

function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== 'undefined' ? localStorage.getItem('sidebar-collapsed') === 'true' : false,
  );
  const toggle = (): void => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });
  };
  return [collapsed, toggle];
}

export function Layout(): JSX.Element {
  const location = useLocation();
  const { user, isAdmin, logout } = useAuth();
  const { preferences, togglePinnedProduct } = usePreferences();
  const pinnedProductIds = preferences?.pinnedProductIds ?? [];
  const [dark, toggleDark] = useDarkMode(preferences?.theme);
  const [sidebarCollapsed, toggleSidebar] = useSidebarCollapsed();
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const initialCollapseSet = useRef(false);
  const { data: teams = [] } = useQuery({ queryKey: queryKeys.teams(), queryFn: api.getTeams });
  const { data: products = [] } = useQuery({ queryKey: queryKeys.products(), queryFn: api.getProducts });

  // Auto-expand pinned products, collapse unpinned (only on initial load)
  useEffect(() => {
    if (initialCollapseSet.current || teams.length === 0) return;
    if (pinnedProductIds.length === 0) return; // no pins → all expanded (default)
    initialCollapseSet.current = true;
    const grouped: Record<string, boolean> = {};
    for (const t of teams) {
      const key = t.productId ?? '__none__';
      if (!grouped.hasOwnProperty(key)) {
        grouped[key] = !pinnedProductIds.includes(key); // collapse if NOT pinned
      }
    }
    setCollapsedGroups(grouped);
  }, [teams, pinnedProductIds]);

  const activeTeamId = location.pathname.match(/\/team\/([^/]+)/)?.[1];

  const toggleGroup = (key: string): void => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="flex h-screen" style={{ background: 'var(--bg)' }}>
      {/* Sidebar */}
      <aside
        className={clsx(
          'flex flex-col border-r shrink-0 transition-all duration-200',
          sidebarCollapsed ? 'w-14' : 'w-56',
        )}
        style={{ background: 'var(--bg-panel)', borderColor: 'var(--border)' }}
      >
        {/* Logo */}
        <div className="px-5 py-5 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2.5">
            <div
              className="w-6 h-6 rounded flex items-center justify-center shrink-0"
              style={{ background: 'var(--accent)', color: '#fff' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="20" x2="18" y2="10"/>
                <line x1="12" y1="20" x2="12" y2="4"/>
                <line x1="6" y1="20" x2="6" y2="14"/>
              </svg>
            </div>
            {!sidebarCollapsed && (
              <span className="text-sm font-semibold tracking-tight" style={{ color: 'var(--text-1)' }}>
                Sprint Pulse
              </span>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 overflow-y-auto">
          <div className="space-y-0.5">
            {[
              { to: '/dashboard', icon: LayoutGrid, label: 'Overview' },
              // { to: '/ask', icon: MessageSquare, label: 'Ask anything' },
            ].map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-2.5 px-3 py-2 rounded text-sm transition-colors',
                    sidebarCollapsed && 'justify-center',
                    isActive
                      ? 'font-medium'
                      : 'opacity-60 hover:opacity-100',
                  )
                }
                style={({ isActive }) => ({
                  background: isActive ? 'var(--bg-subtle)' : 'transparent',
                  color: 'var(--text-1)',
                })}
                title={sidebarCollapsed ? label : undefined}
              >
                <Icon size={14} strokeWidth={1.75} />
                {!sidebarCollapsed && label}
              </NavLink>
            ))}
          </div>

          {/* Teams grouped by product (products without teams still show if they have a roadmap) */}
          {(teams.length > 0 || products.length > 0) && (() => {
            const grouped: Record<string, { label: string; teams: Team[] }> = {};
            // Seed all products so they appear even without teams
            for (const p of products) {
              grouped[p.id] = { label: p.name, teams: [] };
            }
            for (const t of teams) {
              const key = t.productId ?? '__none__';
              if (!grouped[key]) grouped[key] = { label: t.productName ?? 'Other', teams: [] };
              grouped[key].teams.push(t);
            }
            // Sort: pinned first, then alphabetical, "Other" last
            const entries = Object.entries(grouped).sort((a, b) => {
              if (a[0] === '__none__') return 1;
              if (b[0] === '__none__') return -1;
              const aPinned = pinnedProductIds.includes(a[0]);
              const bPinned = pinnedProductIds.includes(b[0]);
              if (aPinned !== bPinned) return aPinned ? -1 : 1;
              return a[1].label.localeCompare(b[1].label);
            });

            return entries.map(([key, group]) => {
              const product = products.find((p: Product) => p.id === key);
              const hasRoadmap = !!product?.jiraRoadmapProjectKey;
              const isRoadmapActive = location.pathname === `/roadmap/${key}`;
              const isGroupCollapsed = !!collapsedGroups[key];

              if (sidebarCollapsed) {
                // In collapsed sidebar, show only icons for teams
                return (
                  <div key={key} className="mt-3 space-y-0.5">
                    {hasRoadmap && (
                      <NavLink
                        to={`/roadmap/${key}`}
                        className={clsx(
                          'flex items-center justify-center px-3 py-2 rounded text-sm transition-colors',
                          isRoadmapActive ? 'font-medium' : 'opacity-60 hover:opacity-100',
                        )}
                        style={{
                          background: isRoadmapActive ? 'var(--bg-subtle)' : 'transparent',
                          color: 'var(--text-1)',
                        }}
                        title={`${product?.name ?? ''} — ${getCurrentQuarterLabel()} Roadmap`}
                      >
                        <MapIcon size={13} strokeWidth={1.75} />
                      </NavLink>
                    )}
                    {group.teams.map((t: Team) => {
                      const isActive = activeTeamId === t.id;
                      return (
                        <NavLink
                          key={t.id}
                          to={`/team/${t.id}`}
                          className={clsx(
                            'flex items-center justify-center px-3 py-2 rounded text-sm transition-colors',
                            isActive ? 'font-medium' : 'opacity-60 hover:opacity-100',
                          )}
                          style={{
                            background: isActive ? 'var(--bg-subtle)' : 'transparent',
                            color: 'var(--text-1)',
                          }}
                          title={t.name}
                        >
                          <Users size={13} strokeWidth={1.75} />
                        </NavLink>
                      );
                    })}
                  </div>
                );
              }

              const isPinned = pinnedProductIds.includes(key);

              return (
              <div key={key} className="mt-5 group/product">
                <div className="flex items-center justify-between px-3 pb-1.5">
                  <button
                    onClick={() => toggleGroup(key)}
                    className="flex items-center gap-1 min-w-0 text-xs font-medium uppercase tracking-widest transition-opacity hover:opacity-80"
                    style={{ color: 'var(--text-3)' }}
                    title={group.label}
                  >
                    <span className="truncate">{group.label}</span>
                    {isGroupCollapsed
                      ? <ChevronRight size={11} />
                      : <ChevronDown size={11} />
                    }
                  </button>
                  {key !== '__none__' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); togglePinnedProduct(key); }}
                      className={clsx(
                        'p-1 rounded transition-opacity',
                        isPinned ? 'opacity-100' : 'opacity-0 group-hover/product:opacity-70 hover:!opacity-100',
                      )}
                      title={isPinned ? 'Unpin from overview' : 'Pin to overview'}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isPinned ? 'var(--accent)' : 'var(--text-3)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 17v5" />
                        <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" fill={isPinned ? 'var(--accent)' : 'none'} />
                      </svg>
                    </button>
                  )}
                </div>
                {!isGroupCollapsed && (
                <div className="space-y-0.5">
                  {hasRoadmap && (
                    <NavLink
                      to={`/roadmap/${key}`}
                      className={clsx(
                        'flex items-center justify-between px-3 py-2 rounded text-sm transition-colors',
                        isRoadmapActive ? 'font-medium' : 'opacity-60 hover:opacity-100',
                      )}
                      style={{
                        background: isRoadmapActive ? 'var(--bg-subtle)' : 'transparent',
                        color: 'var(--text-1)',
                      }}
                    >
                      <span className="flex items-center gap-2">
                        <MapIcon size={13} strokeWidth={1.75} />
                        {getCurrentQuarterLabel()} Roadmap
                      </span>
                      {isRoadmapActive && <ChevronRight size={12} style={{ color: 'var(--text-3)' }} />}
                    </NavLink>
                  )}
                  {group.teams.map((t: Team) => {
                    const isActive = activeTeamId === t.id;
                    return (
                      <NavLink
                        key={t.id}
                        to={`/team/${t.id}`}
                        className={clsx(
                          'flex items-center justify-between px-3 py-2 rounded text-sm transition-colors',
                          isActive ? 'font-medium' : 'opacity-60 hover:opacity-100',
                        )}
                        style={{
                          background: isActive ? 'var(--bg-subtle)' : 'transparent',
                          color: 'var(--text-1)',
                        }}
                      >
                        <span className="flex items-center gap-2">
                          <Users size={13} strokeWidth={1.75} />
                          {t.name}
                        </span>
                        {isActive && <ChevronRight size={12} style={{ color: 'var(--text-3)' }} />}
                      </NavLink>
                    );
                  })}
                </div>
                )}
              </div>
            ); });
          })()}
        </nav>

        {/* Footer */}
        <div className="border-t" style={{ borderColor: 'var(--border)' }}>
          {/* User info */}
          {user && !sidebarCollapsed && (
            <div className="px-3 pt-3 pb-1 flex items-center gap-2">
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-medium"
                style={{ background: 'var(--accent)', color: '#fff' }}
              >
                {(user.name || user.email)?.[0]?.toUpperCase() ?? '?'}
              </div>
              <span className="text-xs truncate" style={{ color: 'var(--text-2)' }}>
                {user.name || user.email}
              </span>
            </div>
          )}

          <div
            className={clsx(
              'px-3 py-3 flex items-center',
              sidebarCollapsed ? 'flex-col gap-2' : 'justify-between',
            )}
          >
            {isAdmin && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors',
                    sidebarCollapsed && 'justify-center',
                    isActive ? 'font-medium' : 'opacity-50 hover:opacity-100',
                  )
                }
                style={{ color: 'var(--text-2)' }}
                title={sidebarCollapsed ? 'Admin' : undefined}
              >
                <Settings size={13} strokeWidth={1.75} />
                {!sidebarCollapsed && 'Admin'}
              </NavLink>
            )}
            <div className={clsx('flex items-center', sidebarCollapsed ? 'flex-col gap-1' : 'gap-1')}>
              <button
                onClick={toggleDark}
                className="p-1.5 rounded transition-opacity opacity-50 hover:opacity-100"
                style={{ color: 'var(--text-2)' }}
                title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {dark ? <Sun size={14} strokeWidth={1.75} /> : <Moon size={14} strokeWidth={1.75} />}
              </button>
              <button
                onClick={logout}
                className="p-1.5 rounded transition-opacity opacity-50 hover:opacity-100"
                style={{ color: 'var(--text-2)' }}
                title="Sign out"
              >
                <LogOut size={14} strokeWidth={1.75} />
              </button>
              <button
                onClick={toggleSidebar}
                className="p-1.5 rounded transition-opacity opacity-50 hover:opacity-100"
                style={{ color: 'var(--text-2)' }}
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {sidebarCollapsed
                  ? <PanelLeft size={14} strokeWidth={1.75} />
                  : <PanelLeftClose size={14} strokeWidth={1.75} />
                }
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}